import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import HomeRpgLogoMark from "../home/rpg/HomeRpgLogoMark";
import {
  GATEWAY_SIDEBAR_FOOTER,
  GATEWAY_SIDEBAR_IDENTITY,
  GATEWAY_SIDEBAR_MAIN,
} from "../../data/dashboardMockData";
import { getAuthToken } from "../../services/authApi";
import { fetchMySupportTicketCounts } from "../../services/supportApi";
import { useAuth } from "../../context/AuthContext";
import { primaryLinkedWallet } from "../../utils/walletDisplay";

function dossierDisplayName(user) {
  if (!user || typeof user !== "object") return "Member";
  const u = String(user.username || "").trim();
  if (u) return u;
  const em = String(user.email || "").trim();
  if (em) return em.split("@")[0] || em;
  return "Member";
}

function GatewaySidebar() {
  const { user } = useAuth();
  const [supportUnread, setSupportUnread] = useState(0);

  const dossier = useMemo(() => {
    const name = dossierDisplayName(user);
    const linked = primaryLinkedWallet(user);
    const hasEmail = Boolean(String(user?.email || "").trim());
    const founderLabel = "Founder tier unavailable";
    const accessRank = linked ? "Wallet linked" : hasEmail ? "Email sign-in" : "Member";
    return { name, founderLabel, accessRank, gatewayStatus: "Active" };
  }, [user]);

  const loadSupportUnread = useCallback(async () => {
    if (!getAuthToken()) {
      setSupportUnread(0);
      return;
    }
    try {
      const c = await fetchMySupportTicketCounts();
      const raw =
        typeof c?.unreadForUser === "number" && !Number.isNaN(c.unreadForUser)
          ? c.unreadForUser
          : typeof c?.unread === "number" && !Number.isNaN(c.unread)
            ? c.unread
            : 0;
      setSupportUnread(Math.max(0, raw));
    } catch {
      setSupportUnread(0);
    }
  }, []);

  useEffect(() => {
    loadSupportUnread();
    const t = setInterval(loadSupportUnread, 90000);
    const bump = () => loadSupportUnread();
    window.addEventListener("treydora-support-tickets-updated", bump);
    return () => {
      clearInterval(t);
      window.removeEventListener("treydora-support-tickets-updated", bump);
    };
  }, [loadSupportUnread]);

  const items = useMemo(() => {
    return GATEWAY_SIDEBAR_MAIN.map((item) => {
      if (item.key !== "support") return item;
      const n = supportUnread;
      if (n <= 0) return { ...item, badge: undefined, badgeTitle: undefined };
      const label = n > 99 ? "99+" : String(n);
      return {
        ...item,
        badge: label,
        badgeTitle: `${n} unread update${n === 1 ? "" : "s"} on your support tickets`,
      };
    });
  }, [supportUnread]);

  return (
    <aside className="dg-sidebar">
      <div className="dg-sidebar__topmark dg-gold-ring">
        <HomeRpgLogoMark />
        <span className="dg-sidebar__topmark-text serif">Treydora</span>
      </div>
      <div className="dg-sidebar__rail">
        {items.map((item) =>
          item.path === "#" ? (
            <button
              key={item.key}
              type="button"
              disabled
              className="dg-sidebar__item dg-sidebar__item--ghost"
              title="Treydora VR client — coming soon"
              aria-label="Treydora VR — coming soon (not available yet)"
            >
              <i className={`fas ${item.icon}`} aria-hidden />
              <span>{item.label}</span>
            </button>
          ) : (
            <NavLink
              key={item.key}
              to={item.path}
              end={
                item.key === "overview" ||
                item.key === "storefront" ||
                item.key === "quests" ||
                item.key === "support"
              }
              className={({ isActive }) => `dg-sidebar__item${isActive ? " is-active" : ""}`}
            >
              <i className={`fas ${item.icon}`} aria-hidden />
              <span className="dg-sidebar__item-label">{item.label}</span>
              {item.badge ? (
                <span className="dg-sidebar__item-badge" title={item.badgeTitle || undefined}>
                  {item.badge}
                </span>
              ) : null}
            </NavLink>
          )
        )}
      </div>
      <div className="dg-sidebar__lower dg-sidebar__lower--watermark">
        <div className="dg-sidebar__dossier dg-gold-ring dg-premium-ring">
          <h3 className="dg-sidebar__dossier-title dg-label-up">Citizen Dossier</h3>
          <p className="dg-sidebar__dossier-name serif">{dossier.name}</p>
          <div className="dg-sidebar__dossier-lines">
            <div>
              <span className="dg-label-up">Founder level</span>
              <span className="dg-sidebar__dossier-val">{dossier.founderLabel}</span>
            </div>
            <div>
              <span className="dg-label-up">Access rank</span>
              <span className="dg-sidebar__dossier-val">{dossier.accessRank}</span>
            </div>
            <div>
              <span className="dg-label-up">Gateway status</span>
              <span className="dg-sidebar__dossier-val dg-sidebar__dossier-val--live">
                <span className="dg-eco-dot dg-eco-dot--live" aria-hidden /> {dossier.gatewayStatus}
              </span>
            </div>
          </div>
        </div>
        <div className="dg-sidebar__identity dg-gold-ring dg-premium-ring">
          <span className="dg-sidebar__identity-mark serif">{GATEWAY_SIDEBAR_IDENTITY.title}</span>
          <span className="dg-sidebar__identity-tag">{GATEWAY_SIDEBAR_IDENTITY.tagline}</span>
        </div>
        <div className="dg-sidebar__foot dg-gold-ring dg-premium-ring">
          <div className="dg-sidebar__foot-label">{GATEWAY_SIDEBAR_FOOTER.clockLabel}</div>
          <div className="dg-sidebar__foot-cycle">{GATEWAY_SIDEBAR_FOOTER.cycle}</div>
          <div className="dg-sidebar__foot-status">
            <span className="dg-pulse-dot" aria-hidden /> System Status: {GATEWAY_SIDEBAR_FOOTER.systemStatus}
          </div>
        </div>
      </div>
    </aside>
  );
}

export default GatewaySidebar;
